import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Persist React state in sessionStorage while keeping standard setter ergonomics.
 * Designed for lightweight UI state (e.g., dropdown selections) that should
 * survive route transitions inside the current browser tab.
 */
export function useSessionState<T>(key: string, initialValue: T | (() => T)) {
  const initialRef = useRef<T>()

  if (initialRef.current === undefined) {
    initialRef.current = typeof initialValue === 'function'
      ? (initialValue as () => T)()
      : initialValue
  }

  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialRef.current as T
    }

    try {
      const raw = window.sessionStorage.getItem(key)
      if (raw === null) {
        return initialRef.current as T
      }
      return JSON.parse(raw) as T
    } catch (error) {
      console.warn(`[useSessionState] Failed to read key "${key}" from sessionStorage`, error)
      return initialRef.current as T
    }
  })

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    try {
      window.sessionStorage.setItem(key, JSON.stringify(value))
    } catch (error) {
      console.warn(`[useSessionState] Failed to persist key "${key}" to sessionStorage`, error)
    }
  }, [key, value])

  const reset = useCallback(() => {
    setValue(initialRef.current as T)
    if (typeof window !== 'undefined') {
      try {
        window.sessionStorage.removeItem(key)
      } catch (error) {
        console.warn(`[useSessionState] Failed to clear key "${key}" from sessionStorage`, error)
      }
    }
  }, [key])

  return [value, setValue, reset] as const
}
